import { Component, ErrorInfo, ReactNode } from "react";
import { Box, Typography } from "@mui/material";
import { signOut } from "firebase/auth";
import { auth } from "@/firebase";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  public static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);

    // Immediately handle error recovery without showing intermediate UI
    if (process.env.NODE_ENV !== "development") {
      this.handleErrorRecovery();
    }
  }

  private handleErrorRecovery = async () => {
    try {
      // Attempt to sign out the user
      await signOut(auth);
      console.log("User signed out due to error");
    } catch (signOutError) {
      console.error("Failed to sign out user:", signOutError);
    }

    // Redirect immediately to login page with error query parameter
    window.location.href = "/?error=app-crash";
  };

  public render() {
    if (this.state.hasError) {
      // Show a minimal loading state while redirecting
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <Typography variant="body1">We've encountered an error.</Typography>
        </Box>
      );
    }

    return this.props.children;
  }
}
